import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, CheckCircle, Clock, AlertCircle, MessageCircle, User, Link, Trash2, RefreshCcw } from 'lucide-react';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { deleteAssignment, fetchAssignments, resetAssignmentState } from '../../redux/assignment/assignment';
import ViewEditTaskPopup from './ViewEditTaskPopup';
import HtmlContentDisplay, { getPlainTextFromHtml, isHtmlContentEmpty } from '../Global/HtmlContentDisplay';

const xorDecrypt = (encrypted, secretKey = '28032002') => {
  try {
    const decoded = atob(encrypted);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ secretKey.charCodeAt(i % secretKey.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
};

const getAuthToken = () => {
  const encryptedToken = Cookies.get('authToken');
  if (!encryptedToken) {
    return null;
  }

  const token = xorDecrypt(encryptedToken);
  if (!token) {
    console.warn('Failed to decrypt auth token');
    return null;
  }

  return token;
};

const Task = ({ tasks, selectedDate, onTaskDeleted, onTaskUpdated }) => {
  const dispatch = useDispatch();
  const [assigneeProfiles, setAssigneeProfiles] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDeleting, setIsDeleting] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const CurrentUid = Cookies.get('userUid');
  const role = Cookies.get('role');
  const isAdmin = role === "Admin Ops";

  const handleRefresh = useCallback(() => {
    console.log("called")
    dispatch(resetAssignmentState());
    dispatch(fetchAssignments());
  }, [dispatch]);

  const handleTaskUpdatedLocal = useCallback((shouldRefresh = false) => {
    console.log('handleTaskUpdatedLocal called with shouldRefresh:', shouldRefresh);

    if (shouldRefresh) {
      console.log('Refreshing tasks from callback...');
      handleRefresh()
      // Trigger a refresh by incrementing the refresh trigger
      setRefreshTrigger(prev => prev + 1);

      // Also call parent callback if needed
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    }
  }, [onTaskUpdated]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      // Use a small timeout to ensure the update is fully processed
      setTimeout(() => {
        console.log('Dispatching refresh actions...');
        dispatch(resetAssignmentState());
        dispatch(fetchAssignments());
      }, 500);
    }
  }, [refreshTrigger, dispatch]);

  const fetchProfile = async (uid) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return;
      }

      const response = await fetch(`https://internalApi.sequoia-print.com/api/profiles/${uid}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAllProfiles = async () => {
      if (!tasks || tasks.length === 0) return;

      // Get unique assignee IDs from all tasks
      const uniqueAssigneeIds = [...new Set(tasks.map(task => task.assignee).filter(Boolean))];

      const profiles = {};
      for (const uid of uniqueAssigneeIds) {
        if (uid) {
          const profileData = await fetchProfile(uid);
          if (profileData) {
            profiles[uid] = profileData;
          }
        }
      }

      setAssigneeProfiles(profiles);
    };

    fetchAllProfiles();
  }, [tasks]);

  const getTasksForDate = (date, taskList) => {
    if (!date || !taskList || !Array.isArray(taskList)) return [];
    const dateStr = date.toDateString();
    return taskList.filter(task => {
      const taskDueDate = task.dueDate ? new Date(task.dueDate) : null;
      return taskDueDate && taskDueDate.toDateString() === dateStr;
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'in-progress':
        return <Clock className="text-blue-500" size={16} />;
      case 'overdue':
        return <AlertCircle className="text-red-500" size={16} />;
      default:
        return <Clock className="text-gray-400" size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'completed': 'bg-green-100 text-green-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'overdue': 'bg-red-100 text-red-800'
    };

    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in-progress':
        return 'in progress';
      case 'completed':
        return 'completed';
      case 'overdue':
        return 'overdue';
      default:
        return status;
    }
  };

  const getAssigneeName = (uid) => {
    const profile = assigneeProfiles[uid];
    if (!profile) return uid;

    const profileData = profile.data || profile;

    if (profileData.firstname || profileData.lastname) {
      return `${profileData.firstname || ''} ${profileData.lastname || ''}`.trim();
    }

    if (profileData.username) {
      return profileData.username;
    }

    return uid;
  };

  // Function to truncate HTML content while preserving structure
  const truncateHtmlContent = (html, wordLimit = 25) => {
    if (!html) return '';
    
    // Get plain text to count words
    const plainText = getPlainTextFromHtml(html);
    const words = plainText.split(' ');
    
    if (words.length <= wordLimit) return html;
    
    // Create a temporary element to parse HTML
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    
    // Function to recursively truncate text content
    const truncateNode = (node, remainingWords) => {
      if (remainingWords <= 0) return 0;
      
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        const words = text.split(' ');
        
        if (words.length <= remainingWords) {
          return remainingWords - words.length;
        } else {
          node.textContent = words.slice(0, remainingWords).join(' ') + '...';
          return 0;
        }
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        for (let i = 0; i < node.childNodes.length && remainingWords > 0; i++) {
          remainingWords = truncateNode(node.childNodes[i], remainingWords);
        }
      }
      
      return remainingWords;
    };
    
    // Truncate the content
    truncateNode(tempElement, wordLimit);
    
    return tempElement.innerHTML;
  };

  const tasksForSelectedDate = getTasksForDate(selectedDate, tasks);

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      setIsDeleting(taskId);
      try {
        await dispatch(deleteAssignment(taskId)).unwrap();

        // Call the parent callback
        if (onTaskDeleted) {
          onTaskDeleted();
        }

        setIsDeleting(null);
      } catch (error) {
        console.error('Failed to delete task:', error);
        setIsDeleting(null);
        // alert('Failed to delete task. Please try again.');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Tasks for {selectedDate ? formatDate(selectedDate) : 'Select a Date'}
        </h2>
        <button
          onClick={handleRefresh}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          title="Refresh tasks"
        >
          <RefreshCcw size={16} />
        </button>
      </div>

      {tasksForSelectedDate.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600">No tasks scheduled for this date</p>
            <p className="text-sm text-gray-500 mt-1">Select a different date to view tasks</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Changed from space-y-4 to grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tasksForSelectedDate.map(task => {
              const canEdit = isAdmin || CurrentUid === task.assignee;

              return (
                <div
                  key={task.id}
                  className={`border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow h-fit ${isDeleting === task.id ? 'opacity-50 pointer-events-none' : ''
                    }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-medium text-gray-900 flex-1 pr-2 text-sm leading-tight">
                      <HtmlContentDisplay 
                        content={truncateHtmlContent(task.title, 25)} 
                        className="task-title-preview"
                      />
                    </div>
                    <div className='flex flex-col gap-2 items-end'>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadge(task.status)}`}>
                        {getStatusText(task.status)}
                      </span>
                      {canEdit && (
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          disabled={isDeleting === task.id}
                          className={`text-red-500 hover:text-red-700 transition-colors ${isDeleting === task.id ? 'cursor-not-allowed opacity-50' : ''
                            }`}
                          title="Delete task"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-1">
                      <User size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate text-xs">{getAssigneeName(task.assignee)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(task.status)}
                      <span className="text-xs">
                        Due {task.dueDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    {task.comment && (
                      <div className="flex items-center gap-1">
                        <MessageCircle size={12} className="text-gray-400 flex-shrink-0" />
                        <span className="text-xs">Comment</span>
                      </div>
                    )}
                    {task.urls && (
                      <div className="flex items-center gap-1">
                        <Link size={12} className="text-gray-400 flex-shrink-0" />
                        <span className="text-xs">Attachment</span>
                      </div>
                    )}
                  </div>

                  {task.comment && (
                    <div className="text-xs text-gray-600 mb-3">
                      <p className="italic line-clamp-2">"{task.comment}"</p>
                    </div>
                  )}

                  {task.urls && (
                    <a
                      href={task.urls}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors block mb-2"
                    >
                      View Attachment
                    </a>
                  )}

                  <button
                    onClick={() => setSelectedTask(task)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors w-full text-left"
                  >
                    View Details
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedTask && (
        <ViewEditTaskPopup
          task={{
            ...selectedTask,
            startDate: new Date(selectedTask.startDate),
            dueDate: new Date(selectedTask.dueDate)
          }}
          onClose={() => setSelectedTask(null)}
          onTaskUpdated={handleTaskUpdatedLocal}
        />
      )}
    </div>
  );
};

export default Task;